import { useEffect, useState } from 'react';
import { Flex } from '@servicetitan/anvil2';
import {
    PrequalificationDecision,
    Reviewing,
    ApplicationState,
    TurnsState,
    DecisionDataStore,
} from '@servicetitan/financing-decision-shared';
import {
    PrequalificationStatus,
    PrequalsApi,
} from '@servicetitan/financing-api-client/src/prequals-api';
import {
    WorkflowStore,
    SetupDataStore,
    useProviderConfig,
    useTurnsIntegration,
} from '@servicetitan/financing-workflow-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import {
    useSecondLookSubmission,
    useLoanStatusPolling,
    useLoanRefresh,
    useLoanSubmission,
} from '@servicetitan/financing-application-workflow-shared';
import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import { usePrequalSubmission, usePrequalStatusPolling } from '../submission/hooks';

const PrequalificationReviewingWorkflowInner = () => {
    const { setPrequalDecisionData } =
        PrequalificationDecision.PrequalificationDecisionDataStore.useStore();
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const [applicationState, setApplicationState] = useState<ApplicationState>('sending');
    const [turnsState, setTurnsState] = useState<TurnsState>('none');
    const [shouldFetchCalculations, setShouldFetchCalculations] = useState(false);
    const [turnsDecision, setTurnsDecision] = useState<SubmitApplicationDecision>(
        SubmitApplicationDecision.Submitted,
    );
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const providerConfig = useProviderConfig();
    const turnsIntegration = useTurnsIntegration();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const [prequalsApi] = useDependencies(PrequalsApi);

    const prequalId = setupData?.prequalificationId;

    const { useFinancingSubmission, hidePendingDecisionScreen } = providerConfig;
    const shouldUseFinancingApi = useFinancingSubmission === true;

    const { isSuccess: isFinancingSubmissionSuccess, loanId } = useLoanSubmission({
        prequalId,
        autoSubmit: shouldUseFinancingApi,
        enabled: shouldUseFinancingApi,
        onSuccess: () => {
            setApplicationState('reviewing');
        },
    });

    const { iovationComponent, isSuccess: isPrequalSubmissionSuccess } = usePrequalSubmission({
        prequalId: prequalId!,
        autoSubmit: !shouldUseFinancingApi,
        onSuccess() {
            setApplicationState('reviewing');
        },
    });

    const isSubmissionSuccess = shouldUseFinancingApi
        ? isFinancingSubmissionSuccess
        : isPrequalSubmissionSuccess;

    // FIX: useLoanRefresh should run for both financing API flows AND hard prequalification flows
    // For GreenSky, it needs to run when submitting loan applications after prequal approval
    // The hook internally checks useFinancingSubmission to determine which API to use
    useLoanRefresh({
        isSubmissionSuccess: shouldUseFinancingApi
            ? isFinancingSubmissionSuccess
            : isPrequalSubmissionSuccess,
        loanId: shouldUseFinancingApi ? (loanId ?? null) : null,
        prequalId: !shouldUseFinancingApi ? (prequalId ?? null) : null,
        isHardPrequalification: true,
    });

    // Second look submission hook - only submitted when main decision is declined
    const {
        submitSecondLook,
        loanId: turnsLoanId,
        isBelowWaterfallThreshold,
        tenantSettingsLoaded,
    } = useSecondLookSubmission({
        autoSubmit: false,
        forceProviderName: 'Turns',
        onSuccess: () => {
            setTurnsState('reviewing');
        },
    });

    // Second look decision polling - when approved, transition to second-look-decision
    useLoanStatusPolling({
        loanId: turnsLoanId,
        onDecisionChange: (decision, loanData) => {
            if (decision === SubmitApplicationDecision.Approved) {
                // Re-set shared stores
                setSelectedPlan(null);
                setDecisionData({
                    ...decisionData!,
                    decision: SubmitApplicationDecision.Approved,
                    loanId: turnsLoanId,
                    approvedAmount:
                        loanData.requestedAmount != null && loanData.approvedAmount != null
                            ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                            : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                    maxCreditAmount: loanData.approvedAmount ?? null,
                    externalLoanId: loanData.externalId ?? '',
                });
                setWorkflowStage('second-look-decision');
            }
            setTurnsDecision(decision);
        },
        onTimeout: () => {
            setTurnsDecision(SubmitApplicationDecision.Declined);
        },
    });

    // Poll for prequalification status - only start after submission succeeds
    const { prequalData } = usePrequalStatusPolling({
        prequalId: prequalId ?? null,
        enabled: isSubmissionSuccess && !shouldUseFinancingApi,
        onStatusChange: status => {
            if (status === PrequalificationStatus.Approved) {
                setApplicationState('approved');
                setShouldFetchCalculations(true);
            } else if (status === PrequalificationStatus.Declined) {
                setApplicationState('declined');
            } else if (status === PrequalificationStatus.Review) {
                setApplicationState('reviewing');
            }
        },
        onTimeout: () => {
            // On timeout, treat as under review
            setApplicationState('reviewing');

            if (hidePendingDecisionScreen) {
                return;
            }

            setPrequalDecisionData({
                externalPrequalId: prequalData?.externalPrequalId ?? null,
                status: PrequalificationStatus.Review,
                approvedPlans: undefined,
            });
            setWorkflowStage('decision');
        },
    });

    // Fetch calculations/approved plans for approved prequalifications
    const { data: calculationsData } = useQuery({
        queryKey: ['prequalCalculations', prequalId],
        queryFn: async () => {
            if (!prequalId) {
                return null;
            }
            const response = await prequalsApi.prequalificationsGetPrequalificationCalculations({
                prequalificationId: prequalId,
            });
            return response.data;
        },
        enabled: shouldFetchCalculations && !!prequalId,
        retry: false,
    });

    // Handle prequalification status changes
    useEffect(() => {
        if (!prequalData?.status) {
            return;
        }

        if (prequalData.status === PrequalificationStatus.Approved) {
            setApplicationState('approved');
        } else if (prequalData.status === PrequalificationStatus.Declined) {
            setApplicationState('declined');

            if (turnsIntegration.isEnabled && tenantSettingsLoaded && !isBelowWaterfallThreshold) {
                setTurnsState('sending');
                submitSecondLook();
            }
        } else if (prequalData.status === PrequalificationStatus.Review) {
            setApplicationState('reviewing');
        }
    }, [
        prequalData?.status,
        turnsIntegration.isEnabled,
        tenantSettingsLoaded,
        isBelowWaterfallThreshold,
        submitSecondLook,
    ]);

    // Handle transition to decision workflow for approved prequalifications
    useEffect(() => {
        if (
            prequalData?.status === PrequalificationStatus.Approved &&
            calculationsData &&
            applicationState === 'approved'
        ) {
            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: calculationsData,
                approvedAmount: prequalData.approvedAmount ?? null,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [prequalData, calculationsData, applicationState, setPrequalDecisionData, setWorkflowStage]);

    // Handle transition to decision workflow for declined prequalifications (when Turns is not enabled or also declined)
    useEffect(() => {
        if (prequalData?.status !== PrequalificationStatus.Declined) {
            return;
        }

        const turnsTransientDecisions = [
            SubmitApplicationDecision.Approved,
            SubmitApplicationDecision.Review,
            SubmitApplicationDecision.Submitted,
        ];

        // Only transition to declined decision if:
        // 1. Turns is not enabled for this business unit, OR
        // 2. Loan amount is below the waterfall threshold, OR
        // 3. Turns decision is final and not approved/review/submitted
        if (
            !turnsIntegration.isEnabled ||
            isBelowWaterfallThreshold ||
            (!turnsTransientDecisions.includes(turnsDecision) && turnsState !== 'none')
        ) {
            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: undefined,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [
        prequalData,
        turnsIntegration.isEnabled,
        isBelowWaterfallThreshold,
        turnsDecision,
        turnsState,
        setPrequalDecisionData,
        setWorkflowStage,
    ]);

    // Handle transition for review status
    useEffect(() => {
        if (
            prequalData?.status === PrequalificationStatus.Review &&
            applicationState === 'reviewing'
        ) {
            if (hidePendingDecisionScreen) {
                return;
            }

            setPrequalDecisionData({
                externalPrequalId: prequalData.externalPrequalId ?? null,
                status: prequalData.status,
                approvedPlans: undefined,
            });

            // Transition to decision stage
            setWorkflowStage('decision');
        }
    }, [
        prequalData,
        applicationState,
        setPrequalDecisionData,
        setWorkflowStage,
        hidePendingDecisionScreen,
    ]);

    // Handle transition to decision workflow when useLoanRefresh updates decisionData
    // This is needed for GoodLeap which uses financing API (useLoanRefresh) instead of prequal polling
    useEffect(() => {
        // Only handle this for financing API flows (GoodLeap)
        if (!shouldUseFinancingApi) {
            return;
        }

        // Don't transition if decision is still Submitted (waiting for decision)
        if (
            !decisionData?.decision ||
            decisionData.decision === SubmitApplicationDecision.Submitted
        ) {
            return;
        }

        // Don't transition if Turns is enabled and we're waiting for Turns decision
        // (but skip this wait if the amount is below the waterfall threshold)
        if (
            turnsIntegration.isEnabled &&
            !isBelowWaterfallThreshold &&
            decisionData.decision === SubmitApplicationDecision.Declined
        ) {
            const turnsTransientDecisions = [
                SubmitApplicationDecision.Approved,
                SubmitApplicationDecision.Review,
                SubmitApplicationDecision.Submitted,
            ];
            // Wait for Turns decision if it's still in progress
            if (turnsTransientDecisions.includes(turnsDecision) && turnsState !== 'none') {
                return;
            }
        }

        // Update application state based on decision
        if (decisionData.decision === SubmitApplicationDecision.Approved) {
            setApplicationState('approved');
        } else if (decisionData.decision === SubmitApplicationDecision.Declined) {
            setApplicationState('declined');
        } else if (decisionData.decision === SubmitApplicationDecision.Review) {
            setApplicationState('reviewing');
            // If hidePendingDecisionScreen is true, stay on reviewing screen instead of transitioning to decision stage
            if (hidePendingDecisionScreen) {
                return;
            }
        }

        // Transition to decision stage
        // WorkflowRenderer will use decisionData directly for GoodLeap (doesn't need prequalDecisionData)
        setWorkflowStage('decision');
    }, [
        shouldUseFinancingApi,
        decisionData?.decision,
        turnsIntegration.isEnabled,
        isBelowWaterfallThreshold,
        turnsDecision,
        turnsState,
        setWorkflowStage,
        hidePendingDecisionScreen,
    ]);

    return (
        <Flex
            direction="column"
            gap="6"
            alignItems="center"
            style={{ width: '100%', margin: '0 auto' }}
        >
            {iovationComponent}
            <div style={{ flexGrow: 1, width: '100%' }}>
                <Reviewing applicationState={applicationState} turnsState={turnsState} />
            </div>
        </Flex>
    );
};

export const PrequalificationReviewingWorkflow = () => <PrequalificationReviewingWorkflowInner />;
